'use client';
import { rgba } from 'polished';
import { type ComponentType, type FC, type SVGProps, useCallback, useEffect, useMemo, useState } from 'react';
import styled, { useTheme } from 'styled-components';

import { TOPBAR_HEIGHT } from '../../atoms';
import { Item, type ItemProps, NavBar, type NavBarProps } from '../../atoms/nav-bar/NavBar';
import { Badge } from '../../molecules/badge/Badge';

export type ItemTabProps = Omit<ItemProps, 'children'> & {
	activityIndicatorText?: string;
	component: ComponentType<{}>;
	disabled?: boolean;
	hasActivityIndicator?: boolean;
	id: string;
	tabTestID?: string;
	text: string;
};

export type TabEndIcon = {
	disabled?: boolean;
	icon?: (props: SVGProps<SVGSVGElement>) => JSX.Element;
	onPress?: () => void;
};

export type TabBarProps = Omit<NavBarProps, 'children'> & {
	backgroundColorVariant?: 'default' | 'light';
	flexVertically?: boolean;
	iconEnd?: TabEndIcon;
	items: Array<ItemTabProps>;
	preserveState?: boolean;
	stickyOffsetTop?: number;
	testID: string;
	variant?: 'fixed' | 'scrollable';
};

export const TABBAR_HEIGHT = 61;
export const TABBAR_ZINDEX = 3;

/**
 * TabBar component
 * @param {boolean} flexVertically
 * @param {() => void} onChangeSelected Method to change tab
 * @param {Array<ItemTabProps>} items array of Items, text, disabled, Id...
 * @param {('fixed' | 'scrollable')} variant Style type
 * @param {TabEndIcon=} iconEnd- The icon that should be displayed on input end + callback
 * @param {string | undefined} defaultSelected defaultSelected tab
 * @param {number | undefined} stickyOffsetTop If there are other sticky elements above the TabBar, this should be the height of those elements
 * @param props Rest of props
 *
 */
export function TabBar({
	backgroundColorVariant = 'default',
	flexVertically = false,
	iconEnd: IconEnd,
	items = [],
	preserveState = false,
	selected,
	stickyOffsetTop = TOPBAR_HEIGHT,
	testID,
	variant = 'fixed',
	...args
}: TabBarProps) {
	const { colors } = useTheme();
	const [selectedIndex, setSelectedIndex] = useState<number>(0);
	const iconSize = 25;
	const scrollable = variant === 'scrollable';
	const hasIcon = IconEnd !== undefined;

	const scrollsPositions = useMemo<Array<number>>(() => new Array(items.length).fill(0), [items]);
	const findIndex = useCallback((selection?: string) => items.findIndex(item => item.id === selection), [items]);

	const handleScroll = useCallback(() => {
		scrollsPositions[selectedIndex] = window.scrollY;
	}, [selectedIndex, scrollsPositions]);

	const setScrollPositionForTab = useCallback(() => {
		if (preserveState) {
			window.scrollTo({ top: scrollsPositions[selectedIndex] });
		}
	}, [selectedIndex, scrollsPositions, preserveState]);

	useEffect(() => {
		window.addEventListener('scroll', handleScroll);

		return () => {
			window.removeEventListener('scroll', handleScroll);
		};
	}, [handleScroll]);

	useEffect(() => {
		const indexOfTab = findIndex(selected);

		setSelectedIndex(indexOfTab);
	}, [selected, findIndex]);

	const tabItemsMapped = useMemo(
		() =>
			items.map(({ activityIndicatorText, disabled, hasActivityIndicator, id, tabTestID, text }) => {
				const isSelected = id === selected;

				return (
					<S.TabItem $scrollable={scrollable} disabled={disabled} id={id} key={id} testID={tabTestID}>
						<S.TabItemWrapper $scrollable={scrollable}>
							<S.Text $selected={isSelected}>{text}</S.Text>
							{hasActivityIndicator && (
								<S.TabIndicatorWrapper $hasText={!!activityIndicatorText} $scrollable={scrollable}>
									<Badge
										label={activityIndicatorText}
										size={activityIndicatorText ? 'default' : 'condensed'}
										variant="red"
									/>
								</S.TabIndicatorWrapper>
							)}
						</S.TabItemWrapper>
					</S.TabItem>
				);
			}),
		[items, selected, scrollable]
	);

	const tabViewsRender = useMemo(() => {
		const TabViewComponent = items[selectedIndex]?.component as unknown as FC;

		return preserveState
			? items.map(({ component }, index) => {
					const ComponentView = component;

					return (
						selectedIndex === index && (
							<S.TabViewItem
								$flexVertically={flexVertically}
								key={index}
								ref={() => setScrollPositionForTab()}
							>
								<ComponentView />
							</S.TabViewItem>
						)
					);
				})
			: TabViewComponent && (
					<S.TabViewItem $flexVertically={flexVertically}>
						<TabViewComponent />
					</S.TabViewItem>
				);
	}, [flexVertically, items, selectedIndex, preserveState, setScrollPositionForTab]);

	return (
		<S.TabViewContainer $flexVertically={flexVertically} data-testid={`${testID}`}>
			<S.TabBarContainer $iconEnd={hasIcon} $scrollable={scrollable} $stickyOffsetTop={stickyOffsetTop}>
				<S.TabBarNavWrapper
					$scrollable={scrollable}
					selected={selected}
					{...args}
					$backgroundColorVariant={backgroundColorVariant}
				>
					{tabItemsMapped}
				</S.TabBarNavWrapper>
				<S.Slider $itemSelected={selectedIndex} $numOfItems={items.length} $scrollable={scrollable} />
				{IconEnd?.icon ? (
					/* eslint-disable react/jsx-pascal-case */
					<S.IconEndContainer
						$iconEnd={hasIcon}
						$iconSize={iconSize}
						$scrollable={scrollable}
						onClick={() => !IconEnd?.disabled && IconEnd?.onPress?.()}
					>
						<IconEnd.icon
							color={IconEnd?.disabled ? colors.text.main.enabled : colors.text.main.enabled}
							height={iconSize}
							width={iconSize}
						/>
					</S.IconEndContainer>
				) : /* eslint-enable react/jsx-pascal-case */
				null}
			</S.TabBarContainer>
			<S.TabViewWrapper $flexVertically={flexVertically} data-testid={`${testID}-${selected}`}>
				{tabViewsRender}
			</S.TabViewWrapper>
		</S.TabViewContainer>
	);
}

namespace S {
	const tabItemWidth = '100px';
	const marginRightWithIcon = 50;

	export const TabViewContainer = styled.div<{ $flexVertically: boolean }>`
		width: 100%;
		position: relative;
		flex: ${({ $flexVertically }) => ($flexVertically ? '1' : 'none')};
		display: flex;
		flex-direction: column;
	`;
	export const TabItem = styled(Item)<{ $scrollable: boolean }>`
		margin: 0px;
		flex-wrap: nowrap;
		padding: ${({ theme }) => `${theme.spacings.xsmall} 0 0`};
		width: ${({ $scrollable }) => ($scrollable ? tabItemWidth : 'auto')};
		white-space: ${({ $scrollable }) => ($scrollable ? 'nowrap' : 'auto')};
		cursor: pointer;
		flex: 1;
	`;

	export const TabItemWrapper = styled.div<{ $scrollable: boolean }>`
		padding: ${({ theme }) => `${theme.spacings.xxsmall} ${theme.spacings.small}`};
		position: relative;
	`;

	export const TabIndicatorWrapper = styled.span<{ $hasText: boolean; $scrollable: boolean }>`
		position: absolute;
		top: 8px;
		right: ${({ $hasText, $scrollable }) => ($hasText ? '-10px' : $scrollable ? '5px' : '0')};
	`;

	export const Text = styled.div<{ $selected: boolean }>`
		padding: 0px;
		color: ${({ $selected, theme }) =>
			$selected ? theme.colors.text.main.enabled : theme.colors.text.main.disabled};
		font-style: normal;
		${({ theme }) => theme.fonts.text.text1};
		text-align: center;
		margin: auto;
		margin-bottom: ${({ theme }) => theme.spacings.xsmall};
	`;

	export const TabBarNavWrapper = styled(NavBar)<{
		$backgroundColorVariant: 'default' | 'light';
		$scrollable: boolean;
	}>`
		margin: 0;
		flex: ${({ $scrollable }) => ($scrollable ? 'none' : '1')};
		justify-content: space-around;

		background-color: ${({ $backgroundColorVariant, theme }) =>
			rgba(
				$backgroundColorVariant === 'light'
					? theme.colors.backgrounds.main.container
					: theme.colors.backgrounds.main.screen,
				0.9
			)};
		backdrop-filter: ${({ $scrollable }) => ($scrollable ? 'none' : 'blur(8px)')};
		-webkit-backdrop-filter: ${({ $scrollable }) => ($scrollable ? 'none' : 'blur(8px)')};
		height: ${TABBAR_HEIGHT}px;
	`;

	export const TabBarContainer = styled.div<{ $iconEnd: boolean; $scrollable: boolean; $stickyOffsetTop: number }>`
		margin: 0;
		flex: none;
		display: flex;
		align-items: center;
		position: ${({ $scrollable }) => ($scrollable ? 'relative' : 'sticky')};
		top: ${({ $scrollable, $stickyOffsetTop }) => ($scrollable ? '0' : `${$stickyOffsetTop}px`)};
		overflow-y: ${({ $scrollable }) => ($scrollable ? 'auto' : 'hidden')};
		-ms-overflow-style: none; /* IE and Edge */
		scrollbar-width: none; /* Firefox */
		&::-webkit-scrollbar {
			display: none; /* Safari and Chrome */
		}
		background-color: transparent;
		margin-right: ${({ $iconEnd, $scrollable }) => (!$scrollable && $iconEnd ? `${marginRightWithIcon}px` : '0')};
		z-index: ${TABBAR_ZINDEX};
	`;

	export const TabViewWrapper = styled.div<{ $flexVertically: boolean }>`
		display: flex;
		width: 100%;
		flex: ${({ $flexVertically }) => ($flexVertically ? '1' : 'none')};
		flex-direction: column;
	`;

	export const TabViewItem = styled.div<{ $flexVertically: boolean }>`
		width: 100%;
		min-height: 100%;
		position: relative;

		display: flex;
		flex: ${({ $flexVertically }) => ($flexVertically ? '1' : 'none')};
		flex-direction: column;
	`;

	export const IconEndContainer = styled.div<{ $iconEnd: boolean; $iconSize: number; $scrollable: boolean }>`
		position: absolute;
		right: ${({ $iconEnd, $iconSize, $scrollable }) =>
			!$scrollable && $iconEnd ? `-${$iconSize / 2 + marginRightWithIcon / 2}px` : '15px'};
		${({ theme }) => theme.breakpoints.queries.only.mobile} {
			position: initial;
		}
	`;

	export const Slider = styled.div<{ $itemSelected: number; $numOfItems: number; $scrollable: boolean }>`
		position: absolute;
		inset-block-end: 0;
		block-size: 2px;
		inline-size: ${({ $numOfItems, $scrollable }) => ($scrollable ? tabItemWidth : `calc(100% / ${$numOfItems})`)};
		background-color: ${({ theme }) => theme.colors.text.brand.enabled};
		inset-inline-start: ${({ $itemSelected, $numOfItems, $scrollable }) =>
			$scrollable
				? `calc((${tabItemWidth} * ${$itemSelected}))`
				: `calc(100% * ${$itemSelected} / ${$numOfItems})`};
		transition: inset-inline-start 0.25s;
	`;
}

export default TabBar;
